import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import Passos, { Passo } from '../components/Passos';

interface Cliente {
  id: number;
  nome: string;
  whatsapp?: string | null;
}

interface Veiculo {
  id: number;
  cliente_id: number | null;
  cliente_nome?: string | null;
  placa: string;
  tipo?: string | null;
  marca?: string | null;
  modelo?: string | null;
  ano?: number | null;
  km_atual?: number | null;
}

interface Material {
  id: number;
  nome: string;
  codigo: string | null;
  unidade_medida: string | null;
  quantidade_atual: string | number;
  valor_unitario: string | number;
  status: string;
}

interface Tecnico {
  id: number;
  nome: string;
  email: string;
  telefone?: string | null;
  whatsapp?: string | null;
  valor_hora?: number | string | null;
  status?: string;
}

interface OrcamentoItem {
  id?: number;
  tipo: 'peca' | 'mao_obra';
  material_id?: number | null;
  material_nome?: string | null;
  tecnico_id?: number | null;
  tecnico_nome?: string | null;
  descricao: string;
  quantidade: number | string;
  valor_unit: number | string;
  total?: number | string;
}

interface FormState {
  id?: number;
  cliente_id: number | null;
  veiculo_id: number | null;
  titulo: string;
  descricao: string;
  observacoes: string;
  prioridade: string;
  itens: OrcamentoItem[];
}

const TIPOS_VEICULO = [
  ['carro', 'Carro'],
  ['moto', 'Moto'],
  ['caminhao', 'Caminhao'],
  ['van', 'Van'],
  ['outro', 'Outro'],
];

const PRIORIDADES = [
  ['baixa', 'Baixa'],
  ['media', 'Media'],
  ['alta', 'Alta'],
  ['urgente', 'Urgente'],
];

const formInicial = (): FormState => ({
  cliente_id: null,
  veiculo_id: null,
  titulo: 'Serviço oficina',
  descricao: '',
  observacoes: '',
  prioridade: 'media',
  itens: [],
});

function erroMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function numero(v: number | string | undefined): number {
  if (v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function moeda(v: number | string | undefined): string {
  return numero(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function inteiroPositivo(v: number | string | undefined): number {
  const n = numero(v);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function totalItem(it: OrcamentoItem): number {
  if (it.tipo === 'mao_obra') return numero(it.valor_unit);
  return numero(it.quantidade) * numero(it.valor_unit);
}

function veiculoLabel(v: Veiculo): string {
  return [v.placa, v.marca, v.modelo, v.ano].filter(Boolean).join(' ');
}

function placa(v: string): string {
  return v.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 10);
}

function vazioParaNull(v?: string | null): string | null {
  const txt = (v || '').trim();
  return txt || null;
}

export default function Orcamentos() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [form, setForm] = useState<FormState>(formInicial());
  const [novoCliente, setNovoCliente] = useState({ nome: '', whatsapp: '' });
  const [novoVeiculo, setNovoVeiculo] = useState({ placa: '', tipo: 'carro', marca: '', modelo: '', ano: '', km_atual: '' });
  const [novoMaterial, setNovoMaterial] = useState({ material_id: '', quantidade: '1' });
  const [novaMaoObra, setNovaMaoObra] = useState({ tecnico_id: '', descricao: '', valor_unit: '0' });
  const [mostrarNovoCliente, setMostrarNovoCliente] = useState(false);
  const [mostrarNovoVeiculo, setMostrarNovoVeiculo] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [salvandoRapido, setSalvandoRapido] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);
  const editando = Boolean(form.id);

  const total = useMemo(
    () => form.itens.reduce((s, it) => s + totalItem(it), 0),
    [form.itens],
  );
  async function carregar() {
    setErro('');
    const [cs, vs, ms, ts] = await Promise.all([
      api<Cliente[]>('/cadastros/clientes.php?somente_ativos=1'),
      api<Veiculo[]>('/cadastros/veiculos.php'),
      api<Material[]>('/estoque/materiais.php'),
      api<Tecnico[]>('/cadastros/tecnicos.php'),
    ]);
    setClientes(cs);
    setVeiculos(vs);
    setMateriais(ms.filter((m) => m.status !== 'inativo'));
    setTecnicos(ts.filter((t) => t.status !== 'inativo'));
  }

  useEffect(() => {
    carregar().catch((e) => setErro(erroMsg(e)));
  }, []);

  function limpar() {
    setForm(formInicial());
    setNovoCliente({ nome: '', whatsapp: '' });
    setNovoVeiculo({ placa: '', tipo: 'carro', marca: '', modelo: '', ano: '', km_atual: '' });
    setNovoMaterial({ material_id: '', quantidade: '1' });
    setNovaMaoObra({ tecnico_id: '', descricao: '', valor_unit: '0' });
    setMostrarNovoCliente(false);
    setMostrarNovoVeiculo(false);
    setWizardKey((k) => k + 1);
  }

  function atualizarItem(idx: number, patch: Partial<OrcamentoItem>) {
    setForm((atual) => ({
      ...atual,
      itens: atual.itens.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  }

  function removerItem(idx: number) {
    setForm((atual) => ({ ...atual, itens: atual.itens.filter((_, i) => i !== idx) }));
  }

  function selecionarMaterial(idx: number, materialId: number | null) {
    if (materialId && form.itens.some((it, i) => i !== idx && it.tipo === 'peca' && it.material_id === materialId)) {
      setErro('Este material já foi adicionado no chamado.');
      return;
    }
    setErro('');
    const material = materiais.find((m) => m.id === materialId);
    atualizarItem(idx, {
      material_id: materialId,
      descricao: material?.nome || '',
      valor_unit: material ? numero(material.valor_unitario) : 0,
    });
  }

  function adicionarMaterialLinha() {
    const materialId = novoMaterial.material_id ? Number(novoMaterial.material_id) : null;
    if (!materialId) {
      setErro('Selecione o material antes de adicionar.');
      return;
    }
    if (form.itens.some((it) => it.tipo === 'peca' && it.material_id === materialId)) {
      setErro('Este material já foi adicionado no chamado.');
      return;
    }
    const quantidade = inteiroPositivo(novoMaterial.quantidade);
    if (!quantidade) {
      setErro('Informe a quantidade do material em número inteiro.');
      return;
    }
    const material = materiais.find((m) => m.id === materialId);
    if (!material) {
      setErro('Material não encontrado.');
      return;
    }
    setErro('');
    setForm({
      ...form,
      itens: [
        ...form.itens,
        {
          tipo: 'peca',
          material_id: material.id,
          material_nome: material.nome,
          descricao: material.nome,
          quantidade,
          valor_unit: numero(material.valor_unitario),
        },
      ],
    });
    setNovoMaterial({ material_id: '', quantidade: '1' });
  }

  function adicionarMaoObraLinha() {
    const tecnicoId = novaMaoObra.tecnico_id ? Number(novaMaoObra.tecnico_id) : null;
    if (!tecnicoId) {
      setErro('Selecione o técnico antes de adicionar.');
      return;
    }
    const tecnico = tecnicos.find((t) => t.id === tecnicoId);
    if (!tecnico) {
      setErro('Técnico não encontrado.');
      return;
    }
    setErro('');
    setForm({
      ...form,
      itens: [
        ...form.itens,
        {
          tipo: 'mao_obra',
          tecnico_id: tecnico.id,
          tecnico_nome: tecnico.nome,
          descricao: novaMaoObra.descricao.trim() || tecnico.nome,
          quantidade: 1,
          valor_unit: numero(novaMaoObra.valor_unit),
        },
      ],
    });
    setNovaMaoObra({ tecnico_id: '', descricao: '', valor_unit: '0' });
  }

  function selecionarTecnico(idx: number, tecnicoId: number | null) {
    const tecnico = tecnicos.find((t) => t.id === tecnicoId);
    atualizarItem(idx, {
      tecnico_id: tecnicoId,
      tecnico_nome: tecnico?.nome || null,
      descricao: tecnico?.nome || '',
      valor_unit: tecnico ? numero(tecnico.valor_hora || 0) : 0,
      quantidade: 1,
    });
  }

  function selecionarNovoTecnico(tecnicoId: string) {
    const tecnico = tecnicos.find((t) => t.id === Number(tecnicoId));
    setNovaMaoObra({
      ...novaMaoObra,
      tecnico_id: tecnicoId,
      descricao: tecnico?.nome || '',
      valor_unit: tecnico ? String(numero(tecnico.valor_hora || 0)) : '0',
    });
  }

  function selecionarVeiculo(veiculoId: number | null) {
    const v = veiculos.find((item) => item.id === veiculoId);
    setForm((atual) => ({ ...atual, veiculo_id: veiculoId, cliente_id: v?.cliente_id || atual.cliente_id || null }));
  }

  async function cadastrarClienteRapido() {
    if (!novoCliente.nome.trim()) { setErro('Informe o nome do cliente.'); return; }
    setErro('');
    setMsg('');
    setSalvandoRapido(true);
    try {
      const r = await api<{ id: number }>('/cadastros/clientes.php', {
        method: 'POST',
        body: {
          nome: novoCliente.nome.trim(),
          whatsapp: vazioParaNull(novoCliente.whatsapp),
          status: 'ativo',
        },
      });
      const cliente: Cliente = { id: r.id, nome: novoCliente.nome.trim(), whatsapp: vazioParaNull(novoCliente.whatsapp) };
      setClientes((atual) => [...atual, cliente]);
      setForm((atual) => ({ ...atual, cliente_id: cliente.id }));
      setNovoCliente({ nome: '', whatsapp: '' });
      setMostrarNovoCliente(false);
      setMsg('Cliente cadastrado.');
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setSalvandoRapido(false);
    }
  }

  async function cadastrarVeiculoRapido() {
    const placaNormalizada = placa(novoVeiculo.placa);
    if (!placaNormalizada) { setErro('Informe a placa do veículo.'); return; }
    setErro('');
    setMsg('');
    setSalvandoRapido(true);
    try {
      const body = {
        cliente_id: form.cliente_id,
        placa: placaNormalizada,
        tipo: novoVeiculo.tipo || 'carro',
        marca: vazioParaNull(novoVeiculo.marca),
        modelo: vazioParaNull(novoVeiculo.modelo),
        ano: novoVeiculo.ano ? Number(novoVeiculo.ano) : null,
        km_atual: novoVeiculo.km_atual ? Number(novoVeiculo.km_atual) : null,
        status: 'ativo',
      };
      const r = await api<{ id: number }>('/cadastros/veiculos.php', { method: 'POST', body });
      const cliente = clientes.find((c) => c.id === form.cliente_id);
      const veiculo: Veiculo = {
        id: r.id,
        cliente_id: form.cliente_id,
        cliente_nome: cliente?.nome || null,
        placa: placaNormalizada,
        tipo: body.tipo,
        marca: body.marca,
        modelo: body.modelo,
        ano: body.ano,
        km_atual: body.km_atual,
      };
      setVeiculos((atual) => [...atual, veiculo]);
      setForm((atual) => ({ ...atual, veiculo_id: veiculo.id }));
      setNovoVeiculo({ placa: '', tipo: 'carro', marca: '', modelo: '', ano: '', km_atual: '' });
      setMostrarNovoVeiculo(false);
      setMsg('Veículo cadastrado.');
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setSalvandoRapido(false);
    }
  }

  function validarVeiculoCliente(): string | null {
    if (!form.veiculo_id) return 'Selecione ou cadastre o veículo.';
    return null;
  }

  function validarServico(): string | null {
    if (!form.titulo.trim()) return 'Informe o serviço do chamado.';
    return null;
  }

  function validarItens(): string | null {
    if (!form.itens.length) return 'Informe ao menos um material ou mão de obra.';
    const materiaisUsados = new Set<number>();
    for (let idx = 0; idx < form.itens.length; idx += 1) {
      const it = form.itens[idx];
      if (it.tipo === 'peca') {
        if (!it.material_id) return `Selecione o material da linha ${idx + 1}.`;
        if (materiaisUsados.has(it.material_id)) return `Material duplicado na linha ${idx + 1}.`;
        materiaisUsados.add(it.material_id);
        if (!inteiroPositivo(it.quantidade)) return `Informe a quantidade inteira do material na linha ${idx + 1}.`;
      }
      if (it.tipo === 'mao_obra' && !it.tecnico_id) return `Selecione o técnico da linha ${idx + 1}.`;
      if (it.tipo === 'mao_obra' && numero(it.valor_unit) < 0) return `Informe o valor da mão de obra na linha ${idx + 1}.`;
    }
    return null;
  }

  async function salvar() {
    const validacao = validarVeiculoCliente() || validarServico() || validarItens();
    if (validacao) { setErro(validacao); return; }
    setErro('');
    setMsg('');
    setSalvando(true);
    const body = {
      id: form.id,
      cliente_id: form.cliente_id,
      veiculo_id: form.veiculo_id,
      titulo: form.titulo.trim(),
      descricao: form.descricao.trim() || null,
      observacoes: form.observacoes.trim() || null,
      prioridade: form.prioridade,
      itens: form.itens.map((it) => ({
        tipo: it.tipo,
        material_id: it.tipo === 'peca' ? (it.material_id || null) : null,
        tecnico_id: it.tipo === 'mao_obra' ? (it.tecnico_id || null) : null,
        descricao: it.descricao.trim(),
        quantidade: it.tipo === 'mao_obra' ? 1 : inteiroPositivo(it.quantidade),
        valor_unit: numero(it.valor_unit),
      })),
    };

    try {
      if (form.id) {
        await api('/oficina/orcamentos.php', { method: 'PUT', body });
        setMsg('Chamado atualizado.');
      } else {
        await api('/oficina/orcamentos.php', { method: 'POST', body });
        setMsg('OS criada.');
        limpar();
        window.location.hash = '#/app/os';
        return;
      }
      limpar();
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setSalvando(false);
    }
  }

  const clienteSelecionado = clientes.find((c) => c.id === form.cliente_id);
  const veiculoSelecionado = veiculos.find((v) => v.id === form.veiculo_id);
  const prioridadeLabel = PRIORIDADES.find(([v]) => v === form.prioridade)?.[1] || form.prioridade;
  const linhasMaterial = form.itens.map((it, idx) => ({ it, idx })).filter(({ it }) => it.tipo === 'peca');
  const linhasMaoObra = form.itens.map((it, idx) => ({ it, idx })).filter(({ it }) => it.tipo === 'mao_obra');
  const materiaisSelecionados = new Set(linhasMaterial.map(({ it }) => it.material_id).filter(Boolean));

  const passos: Passo[] = [
    {
      titulo: 'Cliente e veículo',
      validar: validarVeiculoCliente,
      node: (
        <div className="card">
          <div className="grid cols2">
            <div>
              <label>Cliente</label>
              <select value={form.cliente_id || ''} onChange={(e) => setForm({ ...form, cliente_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">Sem cliente vinculado</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.whatsapp ? ' - ' + c.whatsapp : ''}</option>)}
              </select>
            </div>
            <div>
              <label>Veículo *</label>
              <select value={form.veiculo_id || ''} onChange={(e) => selecionarVeiculo(e.target.value ? Number(e.target.value) : null)} required>
                <option value="">Selecione o veículo</option>
                {veiculos
                  .filter((v) => !form.cliente_id || !v.cliente_id || v.cliente_id === form.cliente_id)
                  .map((v) => <option key={v.id} value={v.id}>{veiculoLabel(v)}{v.cliente_nome ? ' - ' + v.cliente_nome : ''}</option>)}
              </select>
            </div>
          </div>

          {!mostrarNovoCliente ? (
            <button type="button" className="btn sec sm" style={{ marginTop: 10 }} onClick={() => setMostrarNovoCliente(true)}>+ Novo cliente</button>
          ) : (
            <div style={{ border: '1px dashed var(--cinza-borda)', borderRadius: 8, padding: 12, marginTop: 12 }}>
              <h3>Novo cliente</h3>
              <div className="grid cols2">
                <div><label>Nome *</label><input className="input" value={novoCliente.nome} onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })} /></div>
                <div><label>WhatsApp</label><input className="input" value={novoCliente.whatsapp} onChange={(e) => setNovoCliente({ ...novoCliente, whatsapp: e.target.value })} placeholder="(11) 99999-9999" /></div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn sec" onClick={() => { setMostrarNovoCliente(false); setNovoCliente({ nome: '', whatsapp: '' }); }}>Cancelar</button>
                <button type="button" className="btn" disabled={salvandoRapido} onClick={cadastrarClienteRapido}>{salvandoRapido ? 'Salvando...' : 'Salvar cliente'}</button>
              </div>
            </div>
          )}

          {!mostrarNovoVeiculo ? (
            <button type="button" className="btn sec sm" style={{ marginTop: 10, marginLeft: 8 }} onClick={() => setMostrarNovoVeiculo(true)}>+ Novo veículo</button>
          ) : (
            <div style={{ border: '1px dashed var(--cinza-borda)', borderRadius: 8, padding: 12, marginTop: 12 }}>
              <h3>Novo veículo</h3>
              <div className="grid cols2">
                <div><label>Placa *</label><input className="input" value={novoVeiculo.placa} onChange={(e) => setNovoVeiculo({ ...novoVeiculo, placa: placa(e.target.value) })} /></div>
                <div>
                  <label>Tipo</label>
                  <select value={novoVeiculo.tipo} onChange={(e) => setNovoVeiculo({ ...novoVeiculo, tipo: e.target.value })}>
                    {TIPOS_VEICULO.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div><label>Marca</label><input className="input" value={novoVeiculo.marca} onChange={(e) => setNovoVeiculo({ ...novoVeiculo, marca: e.target.value })} /></div>
                <div><label>Modelo</label><input className="input" value={novoVeiculo.modelo} onChange={(e) => setNovoVeiculo({ ...novoVeiculo, modelo: e.target.value })} /></div>
                <div><label>Ano</label><input className="input" type="number" min={0} value={novoVeiculo.ano} onChange={(e) => setNovoVeiculo({ ...novoVeiculo, ano: e.target.value })} /></div>
                <div><label>KM atual</label><input className="input" type="number" min={0} value={novoVeiculo.km_atual} onChange={(e) => setNovoVeiculo({ ...novoVeiculo, km_atual: e.target.value })} /></div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn sec" onClick={() => { setMostrarNovoVeiculo(false); setNovoVeiculo({ placa: '', tipo: 'carro', marca: '', modelo: '', ano: '', km_atual: '' }); }}>Cancelar</button>
                <button type="button" className="btn" disabled={salvandoRapido} onClick={cadastrarVeiculoRapido}>{salvandoRapido ? 'Salvando...' : 'Salvar veículo'}</button>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      titulo: 'Serviço',
      validar: validarServico,
      node: (
        <div className="card">
          <div className="grid cols2">
            <div>
              <label>Serviço *</label>
              <input className="input" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required />
            </div>
            <div>
              <label>Prioridade</label>
              <select value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value })}>
                {PRIORIDADES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>
          <label>Descrição do problema/serviço</label>
          <textarea className="input" rows={4} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
        </div>
      ),
    },
    {
      titulo: 'Materiais',
      validar: validarItens,
      node: (
        <div className="card">
          <div className="orc-section-head">
            <h3>Materiais</h3>
          </div>

          <div className="orc-add-row">
            <div className="orc-add-material">
              <label>Material</label>
              <select value={novoMaterial.material_id} onChange={(e) => setNovoMaterial({ ...novoMaterial, material_id: e.target.value })}>
                <option value="">Selecione o material</option>
                {materiais.map((m) => (
                  <option key={m.id} value={m.id} disabled={materiaisSelecionados.has(m.id)}>
                    {m.nome} - estoque {numero(m.quantidade_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} {m.unidade_medida || ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Quantidade</label>
              <input className="input" type="number" min="1" step="1" value={novoMaterial.quantidade} onChange={(e) => setNovoMaterial({ ...novoMaterial, quantidade: e.target.value })} />
            </div>
            <button type="button" className="btn" onClick={adicionarMaterialLinha}>Adicionar</button>
          </div>

          <div className="tabela-wrap">
            <table className="orc-edit-table">
              <thead><tr><th>Material</th><th>Qtd</th><th>Valor unit.</th><th>Total</th><th></th></tr></thead>
              <tbody>
                {linhasMaterial.map(({ it, idx }) => {
                  const materialAtual = materiais.find((m) => m.id === it.material_id);
                  const linhaTotal = numero(it.quantidade) * numero(it.valor_unit);
                  return (
                    <tr key={`mat-${idx}`}>
                      <td>
                        <select value={it.material_id || ''} onChange={(e) => selecionarMaterial(idx, e.target.value ? Number(e.target.value) : null)} required>
                          <option value="">Selecione o material</option>
                          {materiais.map((m) => (
                            <option key={m.id} value={m.id} disabled={Boolean(materiaisSelecionados.has(m.id) && it.material_id !== m.id)}>
                              {m.nome} - estoque {numero(m.quantidade_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} {m.unidade_medida || ''}
                            </option>
                          ))}
                        </select>
                        {materialAtual && (
                          <small className="orc-help">
                            Estoque: {numero(materialAtual.quantidade_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} {materialAtual.unidade_medida || ''} - Valor cadastrado: {moeda(materialAtual.valor_unitario)}
                          </small>
                        )}
                      </td>
                      <td><input className="input" type="number" min="1" step="1" value={it.quantidade} onChange={(e) => atualizarItem(idx, { quantidade: e.target.value })} /></td>
                      <td><input className="input" type="number" min="0" step="0.01" value={it.valor_unit} onChange={(e) => atualizarItem(idx, { valor_unit: e.target.value })} /></td>
                      <td><strong>{moeda(linhaTotal)}</strong></td>
                      <td className="acoes"><button type="button" className="btn danger sm" onClick={() => removerItem(idx)}>Excluir</button></td>
                    </tr>
                  );
                })}
                {!linhasMaterial.length && <tr><td colSpan={5} className="vazio">Nenhum material adicionado.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="orc-section-head" style={{ marginTop: 16 }}>
            <h3>Mão de obra</h3>
          </div>

          <div className="orc-add-row orc-add-row-labor">
            <div className="orc-add-material">
              <label>Técnico</label>
              <select value={novaMaoObra.tecnico_id} onChange={(e) => selecionarNovoTecnico(e.target.value)}>
                <option value="">Selecione o técnico</option>
                {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nome}{t.valor_hora ? ' - ' + moeda(t.valor_hora) + '/h' : ''}</option>)}
              </select>
            </div>
            <div>
              <label>Valor da mão de obra</label>
              <input className="input" type="number" min="0" step="0.01" value={novaMaoObra.valor_unit} onChange={(e) => setNovaMaoObra({ ...novaMaoObra, valor_unit: e.target.value })} />
            </div>
            <div className="orc-add-description">
              <label>Descrição</label>
              <input className="input" value={novaMaoObra.descricao} onChange={(e) => setNovaMaoObra({ ...novaMaoObra, descricao: e.target.value })} placeholder="Serviço executado" />
            </div>
            <button type="button" className="btn" onClick={adicionarMaoObraLinha}>Adicionar</button>
          </div>

          <div className="tabela-wrap" style={{ marginTop: 10 }}>
            <table className="orc-edit-table">
              <thead><tr><th>Técnico</th><th>Descrição</th><th>Valor da mão de obra</th><th>Total</th><th></th></tr></thead>
              <tbody>
                {linhasMaoObra.map(({ it, idx }) => {
                  const linhaTotal = totalItem(it);
                  return (
                    <tr key={`mo-${idx}`}>
                      <td>
                        <select value={it.tecnico_id || ''} onChange={(e) => selecionarTecnico(idx, e.target.value ? Number(e.target.value) : null)} required>
                          <option value="">Selecione o técnico</option>
                          {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nome}{t.valor_hora ? ' - ' + moeda(t.valor_hora) + '/h' : ''}</option>)}
                        </select>
                      </td>
                      <td><input className="input" value={it.descricao} onChange={(e) => atualizarItem(idx, { descricao: e.target.value })} placeholder="Serviço executado" /></td>
                      <td><input className="input" type="number" min="0" step="0.01" value={it.valor_unit} onChange={(e) => atualizarItem(idx, { valor_unit: e.target.value })} /></td>
                      <td><strong>{moeda(linhaTotal)}</strong></td>
                      <td className="acoes"><button type="button" className="btn danger sm" onClick={() => removerItem(idx)}>Excluir</button></td>
                    </tr>
                  );
                })}
                {!linhasMaoObra.length && <tr><td colSpan={5} className="vazio">Nenhuma mão de obra adicionada.</td></tr>}
              </tbody>
            </table>
          </div>

        </div>
      ),
    },
    {
      titulo: 'Revisão',
      node: (
        <div className="card">
          <h3>Revisão do chamado</h3>
          <div className="os-dados">
            <div className="os-campo"><span className="k">Veículo</span><span className="v">{veiculoSelecionado ? veiculoLabel(veiculoSelecionado) : '-'}</span></div>
            <div className="os-campo"><span className="k">Cliente</span><span className="v">{clienteSelecionado?.nome || '-'}</span></div>
            <div className="os-campo"><span className="k">Serviço</span><span className="v">{form.titulo || '-'}</span></div>
            <div className="os-campo"><span className="k">Prioridade</span><span className="v">{prioridadeLabel}</span></div>
            {form.descricao && <div className="os-campo full"><span className="k">Descrição</span><span className="v bloco">{form.descricao}</span></div>}
            <div className="os-campo"><span className="k">Linhas</span><span className="v">{form.itens.length}</span></div>
            <div className="os-campo"><span className="k">Total</span><span className="v">{moeda(total)}</span></div>
          </div>
          <label>Observações</label>
          <textarea className="input" rows={3} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          <div className="form-actions">
            {editando && <button type="button" className="btn sec" onClick={limpar}>Cancelar edição</button>}
            <button type="button" className="btn" disabled={salvando} onClick={salvar}>{salvando ? 'Salvando...' : (editando ? 'Salvar chamado' : 'Criar chamado')}</button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="titulo-pg">
        <h1>{editando ? 'Editar chamado' : 'Criar chamado'}</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(erroMsg(e)))}>Atualizar</button>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <Passos key={`${form.id || 'novo'}-${wizardKey}`} passos={passos} rotuloFim="Revise e crie o chamado" />
    </div>
  );
}

